import ModalComponent from 'components/Common/Modal';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import { createSectionName } from 'components/Dashboard/helpers/controlActiveFilters';
import { RateUpdateButtons } from 'components/Dashboard/RateUpdatesFeed/RateUpdatesContent';
import useRatingUpdatesFunction from 'components/Dashboard/RateUpdatesFeed/useRatingUpdatesFunction';
import { useMainActionsBarFunctions } from 'components/Dashboard/SubNav/MainListActionsBar';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import transformToPascalCase from 'components/Settings/Utilities/TransformToPascalCase';
import { store } from 'components/Store';
import { useContext, useRef, useState } from 'react';

const RateMultipleUpdatesButton = () => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { selectedUpdates } = state ?? {};
  const [isOpen, setIsOpen] = useState(false);
  return (
    <>
      <button
        className='rounded-pill px-4 specific-y-padding-5 hovered-button bg-main-white general-button text-light-blue nowrap-item'
        onClick={() => setIsOpen(true)}
      >
        <span className='icon-rating font-weight-bold paragraph-p2 specific-right-margin-12' />
        Rate {selectedUpdates?.length === 1 ? 'update' : `updates`}
      </button>
      <RatingMultipleUpdatesModal selectedUpdates={selectedUpdates} openState={{ isOpen, setIsOpen }} />
    </>
  );
};

const RatingMultipleUpdatesModal = ({ selectedUpdates, openState }) => {
  const { isOpen, setIsOpen } = openState ?? {};
  const [loadingButton, setLoadingButton] = useState(null);
  const { contentResults, setContentResults } = useContentResults();
  const { resetSelectedUpdatesState } = useMainActionsBarFunctions();

  const buttonNotRef = useRef(null);
  const buttonQuiteRef = useRef(null);
  const buttonVeryRef = useRef(null);
  const allButtonRefs = [buttonNotRef, buttonQuiteRef, buttonVeryRef];
  const setButtonsDisabled = (disabled) =>
    allButtonRefs.forEach((ref) => ref.current && (ref.current.disabled = disabled));

  const { ratingUpdateFunction } = useRatingUpdatesFunction();
  const rateUpdate = async ({ rating }) => {
    try {
      setLoadingButton(rating);
      setButtonsDisabled(true);
      const updatePromises = selectedUpdates.map((update) =>
        ratingUpdateFunction({
          objectId: update.parentObjectID ?? update.objectID,
          importanceRating: transformToPascalCase(rating),
        })
      );
      const results = await Promise.all(updatePromises);
      const verifyResults = results?.every((result) => result?.status === 200);
      if (!verifyResults) {
        throw new Error('One or more feedback requests failed');
      }
      const sectionName = createSectionName();
      const activeResults = contentResults?.[sectionName];
      if (!activeResults?.feedItemFlags) {
        throw new Error(`Missing feedItemFlags for section "${sectionName}"`);
      }
      const newFeedItemFlags = activeResults?.feedItemFlags;
      const hits = activeResults?.hits ?? [];
      hits?.forEach((item, index) => {
        const update = selectedUpdates?.find(
          (update) => (update.parentObjectID ?? update.objectID) === (item.parentObjectID ?? item.objectID)
        );
        if (update) {
          newFeedItemFlags[index] = {
            ...newFeedItemFlags[index],
            importanceRating: rating,
          };
        }
        return item;
      });
      setContentResults({
        ...contentResults,
        [sectionName]: {
          ...activeResults,
          feedItemFlags: newFeedItemFlags,
        },
      });

      setButtonsDisabled(false);
      setLoadingButton(null);
      setIsOpen(false);
      createNotification('success', 'Feedback added successfully');
      resetSelectedUpdatesState();
    } catch (error) {
      setLoadingButton(null);
      setButtonsDisabled(false);
    }
  };
  return (
    <ModalComponent isOpen={isOpen} setIsOpen={setIsOpen} overlayClassName='react-modal-overlay adjustment-of-position'>
      <div className='p-4'>
        <div className='px-1 pb-3'>
          <RateUpdateButtons
            selectedUpdates={selectedUpdates}
            rating={''}
            rateUpdate={rateUpdate}
            buttonRefs={{ buttonNotRef, buttonQuiteRef, buttonVeryRef }}
            loadingButton={loadingButton}
            keyboardFunctionality={false}
          />
        </div>
      </div>
    </ModalComponent>
  );
};

export default RateMultipleUpdatesButton;
